import { ImageStoreBaseInformation
       } from 'app/core/imagestore/imagestore.service';
import { GalleryConstants, GlobalConstants } from 'core/constants';
import { TrivialError } from 'core/error/error';
import { Globals } from 'core/globals';
import { ImageStoreRequestError } from 'core/imagestore/imagestore.service';
import { Utils } from 'core/utils';
import { GalleryImage } from './gallery.service';

export type ImageProvider = (id: string) => Promise<GalleryImage>;

export class ImageProvisionError extends TrivialError
{
  // i18n.error.imageProvisionError.title
  // i18n.error.imageProvisionError.message
  protected _name: string = 'ImageProvisionError';
}

export class ImageUploadError extends TrivialError
{
  // i18n.error.imageUploadError.title
  // i18n.error.imageUploadError.message
  protected _name: string = 'ImageUploadError';
}

export class UnsupportedMediaTypeError extends TrivialError
{
  // i18n.error.unsupportedMediaTypeError.title
  // i18n.error.unsupportedMediaTypeError.message
  protected _name: string = 'UnsupportedMediaTypeError';
}

export class ImageSizeLimitExceededError extends TrivialError
{
  // i18n.error.imageSizeLimitExceededError.title
  // i18n.error.imageSizeLimitExceededError.message
  protected _name: string = 'ImageSizeLimitExceededError';
}

export interface UploadProgressMap
{
  [jobIndex: number]: number;
}

export function toImageStoreError(
  error: ImageStoreRequestError, cause: string): TrivialError
{
  const message =
    `[${error.status} ${error.statusText}] ${error.detail}`.trim();

  const detail = [cause];

  switch (error.status) {
    case 400: {
      if (message.match(/.* limit.*exceeded/)) {
        return new ImageSizeLimitExceededError(message, detail);
      } else {
        return new ImageProvisionError(message, detail);
      }
    }
    case 404: return new ImageUploadError(message, detail);
    case 415: return new UnsupportedMediaTypeError(message, detail);
    case 422: return new ImageProvisionError(message, detail);
    default: return new ImageProvisionError(message, detail);
  }
}

export function toContentTypeError(contentType: string, cause: string):
    TrivialError
{
  return new UnsupportedMediaTypeError(
    `Content Type is not allowed: ${contentType}`, [cause]);
}

export function fileHash(file: File): string
{
  return Utils.sha256sum(file.name + file.size);
}

export function locationHash(location: string): string
{
  return Utils.sha256sum(location);
}

export function importImageStoreImage(
  info: ImageStoreBaseInformation): ImageProvider
{
  return async (id: string) => {
    await checkContentType(info.contentType, info.location);

    return {
      url: info.location,
      width: info.width,
      height: info.height,
      id: id,
      hash: locationHash(info.location),
      contentCenter: undefined,
      expirationDate: info.expirationDate,
    };
  };
}

/**
 * @throws ImageUploadError
 * @throws UnsupportedMediaTypeError
 * @throws ImageProvisionError
 */
export async function checkContentType(contentType: string, cause: string
                                      ): Promise<void>
{
  const isAcceptableContentType =
    Globals.mode === GlobalConstants.MODE_GOD ||
    GalleryConstants.CONTENT_TYPE_BLACKLIST.indexOf(contentType) === -1;

  if (! isAcceptableContentType) {
    throw toContentTypeError(contentType, cause);
  }
}



// WEBPACK FOOTER //
// ./src/app/gallery/imageprovider.ts